/*
  PrüfDoc Pro – Seite "Abo" im Einstellungsbereich (React).
  Zeigt Tarif und Status, startet den Stripe-Checkout und öffnet das Kundenportal.
  Route /einstellungen/abo. Anmeldung: über die vorhandene api-Instanz (Session-Cookie).
*/
import { useEffect, useState } from 'react';
import api from '@/lib/api';

const TARIFE = [
  { plan: 'einzel', name: 'Einzel', preis: '39 €', jahr: '468 € im Jahr', punkte: ['1 Nutzer', 'Pläne, Mängel und Tickets', 'Offline-Modus', 'PDF-Berichte mit eigenem Logo'] },
  { plan: 'team', name: 'Team', preis: '99 €', jahr: '1.188 € im Jahr', punkte: ['Bis 8 Nutzer, 1 Admin', 'Unbegrenzt viele Objekte', 'Aufgaben zuweisen', 'Dashboard über alle Objekte'], hervorheben: true },
];
const STATUS_TEXT = {
  trialing: 'Testphase',
  active: 'Aktiv',
  past_due: 'Zahlung offen',
  unpaid: 'Unbezahlt',
  rechnung_offen: 'Rechnung noch nicht bezahlt',
  unbezahlt: 'Rechnung nicht bezahlt',
  canceled: 'Gekündigt',
  incomplete: 'Zahlung nicht abgeschlossen',
  incomplete_expired: 'Abgelaufen',
  paused: 'Pausiert',
};
const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');
const knopf = { minHeight: 44, padding: '10px 16px', borderRadius: 8, border: 'none', background: '#E8650A', color: '#fff', fontWeight: 700, cursor: 'pointer' };

export default function AboVerwaltung() {
  const [info, setInfo] = useState(null);
  const [laedt, setLaedt] = useState('');
  const [fehler, setFehler] = useState('');
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    // Nach der Rückkehr von Stripe direkt abgleichen (falls der Webhook noch unterwegs ist).
    const req = params.get('erfolg') ? api.post('/billing/sync') : api.get('/billing/status');
    req.then((r) => setInfo(r.data)).catch(() => setFehler('Der Abo-Status konnte nicht geladen werden.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function weiter(pfad, body) {
    setFehler('');
    setLaedt(pfad + (body ? body.plan : ''));
    try {
      const { data: d } = await api.post(`/billing/${pfad}`, body);
      if (!d.url) throw new Error('Unbekannter Fehler');
      window.location.href = d.url;          // weiter zu Stripe
    } catch (e) {
      setFehler(e.response?.data?.detail || e.message);
      setLaedt('');
    }
  }

  if (!info) {
    return <div style={{ padding: 32 }} data-testid="abo-loading">{fehler || 'Lädt …'}</div>;
  }
  const hatAbo = info.plan && info.plan !== 'test';
  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 20px', display: 'flex', flexDirection: 'column', gap: 24 }} data-testid="abo-page">
      <h1 style={{ margin: 0 }} className="text-4xl sm:text-5xl font-head font-extrabold tracking-tight">Abo</h1>
      {params.get('erfolg') && (
        <div style={{ padding: 14, borderRadius: 8, background: '#E8F7EF', color: '#13784A' }} data-testid="abo-success">
          Vielen Dank! Ihr Abo ist eingerichtet. Es kann einige Sekunden dauern, bis der Status hier erscheint.
        </div>
      )}
      {info.zahlung_offen && (
        <div style={{ padding: 14, borderRadius: 8, background: '#FFF4E5', color: '#9A5B00' }} data-testid="abo-payment-due">
          Die letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsart über „Abo verwalten“.
        </div>
      )}
      {fehler && <div role="alert" style={{ padding: 14, borderRadius: 8, background: '#FDECEC', color: '#9B1C1C' }} data-testid="abo-error">{fehler}</div>}
      <section style={{ border: '1px solid #D5DCE2', borderRadius: 10, padding: 20 }} data-testid="abo-status">
        {info.betreiber ? (
          <p style={{ margin: 0 }}>Betreiber-Konto: voller Zugriff ohne Abo.</p>
        ) : hatAbo ? (
          <>
            <p style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>
              Tarif {info.plan.charAt(0).toUpperCase() + info.plan.slice(1)} · {STATUS_TEXT[info.subscription_status] || info.subscription_status}
            </p>
            {info.laufzeit_bis && (
              <p style={{ margin: '0 0 14px', color: '#55606B' }}>
                {info.kuendigung_zum_laufzeitende ? 'Endet am ' : 'Verlängert sich am '}{datum(info.laufzeit_bis)}
              </p>
            )}
            <button onClick={() => weiter('portal')} disabled={!!laedt} style={knopf} data-testid="abo-portal-btn">
              {laedt === 'portal' ? 'Öffnet …' : 'Abo verwalten (Tarif, Zahlungsart, Rechnungen, Kündigung)'}
            </button>
          </>
        ) : info.plan === 'test' ? (
          <p style={{ margin: 0 }}>Ihre kostenlose Testphase läuft bis <strong>{datum(info.test_endet)}</strong>. Wenn Sie jetzt buchen, zahlen Sie erst ab Testende.</p>
        ) : (
          <p style={{ margin: 0 }}>Ihre Testphase ist beendet. Ihre Daten bleiben erhalten – wählen Sie einen Tarif, um weiterzuarbeiten.</p>
        )}
      </section>
      {!hatAbo && !info.betreiber && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {TARIFE.map((t) => (
            <div key={t.plan} style={{ border: `1.5px solid ${t.hervorheben ? '#E8650A' : '#D5DCE2'}`, borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid={`abo-tarif-${t.plan}`}>
              <strong style={{ fontSize: 20 }}>{t.name}</strong>
              <div><span style={{ fontSize: 32, fontWeight: 800 }}>{t.preis}</span> im Monat</div>
              <small style={{ color: '#55606B' }}>jährlich abgerechnet, {t.jahr} · gem. § 19 UStG ohne USt.</small>
              <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc' }}>{t.punkte.map((p) => <li key={p}>{p}</li>)}</ul>
              <button onClick={() => weiter('checkout', { plan: t.plan })} disabled={!!laedt} style={{ ...knopf, marginTop: 'auto' }} data-testid={`abo-buy-${t.plan}`}>
                {laedt === 'checkout' + t.plan ? 'Weiter zu Stripe …' : `${t.name} buchen`}
              </button>
            </div>
          ))}
          <div style={{ border: '1.5px solid #D5DCE2', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="abo-tarif-individuell">
            <strong style={{ fontSize: 20 }}>Individuell</strong>
            <div><span style={{ fontSize: 32, fontWeight: 800 }}>ab 249 €</span> im Monat</div>
            <small style={{ color: '#55606B' }}>+ einmalig 490 – 990 € Einrichtung</small>
            <p style={{ margin: 0 }}>Unbegrenzt Nutzer, mehrere Admins, komplette Einrichtung durch uns.</p>
            <a href="https://info.pruefdoc-pro.de/#preise" style={{ marginTop: 'auto', color: '#E8650A', fontWeight: 700 }} data-testid="abo-individuell-link">Anfrage stellen</a>
          </div>
        </div>
      )}
    </div>
  );
}
